// Implements: F-ITK-007, F-UIX-007
/**
 * The command to send, read-only, with its copy action. Pure presentation.
 *
 * The copy goes through the Clipboard API inside a `try/catch` (checklist 15): a browser with no
 * API, no permission or no secure origin gets the command SELECTED in its field and a notice to
 * copy it by hand. The command lives in component state only, never in a browser store. The
 * confirmation is temporary, and its timer is injected so no test sleeps (checklist 16).
 */

import { useEffect, useId, useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import styles from "./WhatsApp.module.css";

/** Runs `callback` later and returns what cancels it. Injected so no test sleeps. */
export type Schedule = (callback: () => void, delayMs: number) => () => void;

const NOTICE_MS = 2000;

const defaultSchedule: Schedule = (callback, delayMs) => {
  const handle = setTimeout(callback, delayMs);
  return () => {
    clearTimeout(handle);
  };
};

export interface IssuedCommandProps {
  readonly command: string;
  readonly schedule?: Schedule | undefined;
}

type Notice = "copied" | "manual" | null;

export function IssuedCommand({ command, schedule = defaultSchedule }: IssuedCommandProps): ReactElement {
  const { t } = useTranslation();
  const id = useId();
  const fieldRef = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const cancel = useRef<(() => void) | null>(null);

  useEffect(() => {
    // The command has just appeared: the focus goes to it, ready to be copied.
    fieldRef.current?.focus();
    return () => {
      cancel.current?.();
    };
  }, []);

  const copy = async (): Promise<void> => {
    let next: Notice = "copied";
    try {
      await navigator.clipboard.writeText(command);
    } catch {
      next = "manual";
      fieldRef.current?.focus();
      fieldRef.current?.select();
    }
    cancel.current?.();
    setNotice(next);
    if (next === "copied") {
      cancel.current = schedule(() => {
        cancel.current = null;
        setNotice(null);
      }, NOTICE_MS);
    }
  };

  return (
    <div className={styles.meta}>
      <label htmlFor={id} className={styles.label}>
        {t("intake.code.field")}
      </label>
      <div className={styles.row}>
        <input id={id} ref={fieldRef} className={styles.command} type="text" value={command} readOnly />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void copy();
          }}
        >
          {t("intake.code.copy")}
        </Button>
      </div>
      <p role="status" className={styles.notice}>
        {notice === "copied" ? t("intake.code.copied") : null}
        {notice === "manual" ? t("intake.code.copyManual") : null}
      </p>
    </div>
  );
}
